import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Slider,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";

// Formik & yup functions
import { useFormik, getIn, FormikProps } from "formik";
import * as Yup from "yup";

// Types & enums
import { EControlLabels } from "../../../components/masterData/FormBuilder/controls/controlList/ControlListTypes";
import {
  IDynamicFormProps,
  IQuestion,
  ISection,
  ISubFroms,
} from "./CommonDynamicFormTypes";
import { fileTypeExtensionMap } from "../../../components/masterData/FormBuilder/controlPropertyModals/fileUploadModal/FileUploadModalTypes";

// Loader component
import Loader from "../../layout/Loader";

// Validation schema
import { activityFormValidationSchema } from "./CommonDynamicFormValidationSchema";

// Multi-lingual support
import { t } from "i18next";
import CommonFileUploadHint from "../commonFileUploadInfo/CommonFileUploadInfo";

// Hooks
import { useToaster } from "../../../hooks/useToasterContext/useToasterContext";

// Constants
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// AWS Manager
import {
  generateUploadRequest,
  getMediaSignedData,
  uploadToAWSServer,
} from "../../../services/awsManager/AwsManager";

// API Manager
import { getApi } from "../../../services/apiManager/ApiManager";

// Reusable DynamicForm component
const DynamicForm: React.FC<IDynamicFormProps> = ({
  formData,
  onSubmit,
  setValidateStep,
  isHideSubmitBtn,
  sxControlStyle,
  isDisableAllFields,
  onCancel,
}) => {
  // Set loader state
  const [isLoading, setIsLoading] = useState(false);
  // Store all tiny forms data
  const [allTinyForms, setAllTinyForms] = useState<{ [key: string]: any }>({});
  // Store file names for display
  const [fileNames, setFileNames] = useState<{ [key: string]: string }>({});

  // Toaster for error messages
  const { setShowToaster } = useToaster();

  // Utility function to get accept string for file input
  const getAcceptStringFromFileTypes = (
    fileTypes: string[] = [],
  ): string | undefined => {
    if (!fileTypes.length) return undefined;
    return fileTypes
      .flatMap((type) =>
        fileTypeExtensionMap[type] ? fileTypeExtensionMap[type] : [type],
      )
      .map((ext) => `.${ext}`)
      .join(",");
  };

  // Generate validation schema dynamically
  const validationSchema = useMemo(() => {
    return formData.sections
      ? activityFormValidationSchema()
      : Yup.object().shape({});
  }, [formData.sections]);

  // Transform sections
  const transformSections = async (
    sections: ISection[],
  ): Promise<ISection[]> => {
    const transformedSections = await Promise.all(
      sections.map(async (section, sectionIndex) => ({
        ...section,
        questions: await Promise.all(
          section.questions.map(async (question: IQuestion, questionIndex) => {
            let fileName = "";

            if (
              question.typeOfControl === EControlLabels.FILE_UPLOAD &&
              question.answer
            ) {
              const mediaData = await getMediaSignedData(question.answer);
              if (mediaData) {
                fileName = mediaData.fileName ?? "";

                // Set fileName for display immediately during transformation
                const fieldName = `sections[${sectionIndex}].questions[${questionIndex}].answer`;
                setFileNames((prev) => ({ ...prev, [fieldName]: fileName }));

                return {
                  ...question,
                  answer: question.answer,
                  answerId: question.answerId ?? "",
                  subForms: question.subForms ?? [],
                };
              }
            }

            // Transform subforms to handle file uploads in tiny forms
            const transformedSubForms = await Promise.all(
              (question.subForms ?? []).map(async (subForm, subFormIndex) => ({
                ...subForm,
                sections: await Promise.all(
                  subForm.sections.map(async (subSection, subSectionIndex) => ({
                    ...subSection,
                    questions: await Promise.all(
                      subSection.questions.map(
                        async (subQuestion, subQuestionIndex) => {
                          let subFileName = "";

                          if (
                            subQuestion.typeOfControl ===
                              EControlLabels.FILE_UPLOAD &&
                            subQuestion.answer
                          ) {
                            const subMediaData = await getMediaSignedData(
                              subQuestion.answer,
                            );
                            if (subMediaData) {
                              subFileName = subMediaData.fileName ?? "";

                              // Set fileName for display for subform file uploads
                              const subFieldName = `sections[${sectionIndex}].questions[${questionIndex}].subForms[${subFormIndex}].sections[${subSectionIndex}].questions[${subQuestionIndex}].answer`;
                              setFileNames((prev) => ({
                                ...prev,
                                [subFieldName]: subFileName,
                              }));
                            }
                          }

                          return {
                            ...subQuestion,
                            answer: subQuestion.answer ?? "",
                            answerId: subQuestion.answerId ?? "",
                            subForms: subQuestion.subForms ?? [],
                          };
                        },
                      ),
                    ),
                  })),
                ),
              })),
            );

            return {
              ...question,
              answer: question.answer ?? "",
              answerId: question.answerId ?? "",
              subForms: transformedSubForms,
            };
          }),
        ),
      })),
    );

    return transformedSections;
  };

  // Formik initialization
  const [initialValues, setInitialValues] = useState({ sections: [] as any });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true, // important to allow reinit when state updates
    validationSchema,
    onSubmit: (values) => {
      onSubmit({ formData: values });
    },
  });

  useEffect(() => {
    const init = async () => {
      const transformed: any =
        (await transformSections(formData.sections)) ?? [];
      setInitialValues({ sections: transformed });
    };
    init();
  }, [formData.sections]);

  // Fetch all tiny forms data
  useEffect(() => {
    const fetchAllTinyForms = async () => {
      const allTinyFormIds = new Set<string>();
      formData.sections?.forEach((section) => {
        section.questions?.forEach((question) => {
          if (question.data?.option) {
            question.data.option.forEach((option) => {
              if (option.tinyForms) {
                option.tinyForms.forEach((formId) =>
                  allTinyFormIds.add(formId),
                );
              }
            });
          }
        });
      });

      // If no tiny forms
      if (allTinyFormIds.size === 0) {
        return;
      }

      setIsLoading(true);
      try {
        const tinyFormsData = await Promise.all(
          Array.from(allTinyFormIds).map(async (formId) => {
            const response = await getApi(
              `/v1/questionnaire/form?form_id=${formId}`,
            );
            return { [formId]: response.status ? response.data : null };
          }),
        );
        const lookup = Object.assign({}, ...tinyFormsData);
        setAllTinyForms(lookup);
      } catch (error) {
        console.error("Error fetching tiny forms:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllTinyForms();
  }, [formData.sections]);

  // Expose submitForm to the parent via setValidateStep (for validation and submission)
  useEffect(() => {
    if (setValidateStep) {
      setValidateStep(() => formik.submitForm); // Passing submitForm to parent
    }
  }, [formik.submitForm, setValidateStep]);

  // Upload file to AWS S3 and set the file guid in formik
  const uploadSeletcedFile = async (fileData: File, fieldName: string) => {
    setIsLoading(true);
    try {
      // Check file size limit (5MB)
      if (fileData.size > MAX_FILE_SIZE) {
        setShowToaster(
          true,
          t("addUpdateField.mediaFile.imageFileSizeLimit"),
          "error",
        );
        setIsLoading(false);
        return;
      }
      const fileUploadRequestData = await generateUploadRequest(fileData);
      if (fileUploadRequestData && fileUploadRequestData.s3SignedUrl) {
        await uploadToAWSServer(fileUploadRequestData.s3SignedUrl, fileData);
        // Store fileId in form field for submission
        formik.setFieldValue(fieldName, fileUploadRequestData.fileId);
        // Store fileName for display purposes
        setFileNames((prev) => ({ ...prev, [fieldName]: fileData.name }));
      } else {
        formik.setFieldValue(fieldName, null);
        // Clear fileName from display state
        setFileNames((prev) => {
          const newState = { ...prev };
          delete newState[fieldName];
          return newState;
        });
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle single choice selection for tiny form
  const handleSingleChoiceSelectionTinyForm = (
    value: string,
    formIndex: number,
    sectionIndex: number,
    questionIndex: number,
    tinySectionIndex: number,
    tinyQuestionIndex: number,
  ) => {
    // ✅ Build the correct path in Formik's state
    const fieldName = `sections[${sectionIndex}].questions[${questionIndex}].subForms[${formIndex}].sections[${tinySectionIndex}].questions[${tinyQuestionIndex}].answer`;

    // ✅ Update Formik state properly
    formik.setFieldValue(fieldName, value);
  };

  // Handle single choice selection for main form
  const handleSingleChoiceSelection = (
    value: string,
    sectionIndex: number,
    questionIndex: number,
  ) => {
    // ✅ Update answer
    formik.setFieldValue(
      `sections[${sectionIndex}].questions[${questionIndex}].answer`,
      value,
    );

    // Handle subform load functionality & bind the tiny form to the main form here
    const selectedOption = formik.values.sections?.[sectionIndex]?.questions?.[
      questionIndex
    ]?.data?.option?.find((option: any) => option.value === value);

    // If no tiny forms, clear subForms
    if (!selectedOption?.tinyForms?.length) {
      formik.setFieldValue(
        `sections[${sectionIndex}].questions[${questionIndex}].subForms`,
        [],
      );
      return;
    }

    const subFormsData = selectedOption.tinyForms
      .map((formId: string) => allTinyForms[formId])
      .filter((form: any) => form !== null)
      .map((form: any) => mapIQuestionaryToSubFrom(form));

    // Set the valid subForms data
    formik.setFieldValue(
      `sections[${sectionIndex}].questions[${questionIndex}].subForms`,
      subFormsData,
    );
  };

  // Render tiny form question
  const getRenderedTinyFormQuestion = (
    question: IQuestion,
    formIndex: number,
    sectionIndex: number,
    questionIndex: number,
    tinySectionIndex: number,
    tinyQuestionIndex: number,
  ): JSX.Element | null => {
    // ✅ Get the currently selected answer for the Single Choice question
    const selectedOption =
      formik.values.sections?.[sectionIndex]?.questions?.[questionIndex]
        ?.answer;

    if (!selectedOption) return null; // If no option is selected, don't render tiny forms.

    // ✅ Find the selected option object
    const selectedOptionObj = formik.values.sections?.[
      sectionIndex
    ]?.questions?.[questionIndex]?.data.option?.find(
      (option: any) => option.value === selectedOption,
    );

    if (!selectedOptionObj || !selectedOptionObj.tinyForms) return null; // No tiny form data, return null.

    // ✅ Define the path to store the tiny form answer
    const fieldName = `sections[${sectionIndex}].questions[${questionIndex}].subForms[${formIndex}].sections[${tinySectionIndex}].questions[${tinyQuestionIndex}].answer`;

    // ✅ Check if there's an error
    const hasError =
      getIn(formik.errors, fieldName) && getIn(formik.touched, fieldName);

    switch (question.typeOfControl) {
      case EControlLabels.SHORT_ANSWER:
        return (
          <Box key={fieldName} mb={2}>
            <Typography>{question.data.question}</Typography>
            <TextField
              fullWidth
              variant="filled"
              hiddenLabel
              size="small"
              placeholder={t("commonDynamicForm.textinputPlaceholder")}
              {...formik.getFieldProps(fieldName)}
              error={!!hasError}
              helperText={hasError && getIn(formik.errors, fieldName)}
              sx={sxControlStyle}
              disabled={isDisableAllFields}
            />
          </Box>
        );

      case EControlLabels.LONG_ANSWER:
        return (
          <Box key={fieldName} mb={2}>
            <Typography>{question.data.question}</Typography>
            <TextField
              variant="filled"
              hiddenLabel
              size="small"
              multiline
              rows={4}
              placeholder={t("commonDynamicForm.textinputPlaceholder")}
              {...formik.getFieldProps(fieldName)}
              error={!!hasError}
              helperText={hasError && getIn(formik.errors, fieldName)}
              sx={sxControlStyle}
              disabled={isDisableAllFields}
            />
          </Box>
        );

      case EControlLabels.SINGLE_CHOICE:
        return (
          <Box key={fieldName} mb={2}>
            <Typography>{question.data.question}</Typography>
            {question.data.option && question.data.option.length <= 3 ? (
              <RadioGroup
                name={fieldName}
                value={
                  formik.values?.sections?.[sectionIndex]?.questions?.[
                    questionIndex
                  ]?.subForms?.[formIndex]?.sections?.[tinySectionIndex]
                    ?.questions?.[tinyQuestionIndex]?.answer || ""
                }
                onChange={(event) =>
                  handleSingleChoiceSelectionTinyForm(
                    event.target.value,
                    formIndex,
                    sectionIndex,
                    questionIndex,
                    tinySectionIndex,
                    tinyQuestionIndex,
                  )
                }
              >
                {question.data.option?.map((option, index) => (
                  <FormControlLabel
                    key={index}
                    value={option.value}
                    control={<Radio />}
                    label={option.value}
                    disabled={isDisableAllFields}
                  />
                ))}
              </RadioGroup>
            ) : (
              <FormControl variant="filled" fullWidth margin="normal">
                <Select
                  name={fieldName}
                  value={
                    formik.values?.sections?.[sectionIndex]?.questions?.[
                      questionIndex
                    ]?.subForms?.[formIndex]?.sections?.[tinySectionIndex]
                      ?.questions?.[tinyQuestionIndex]?.answer || ""
                  }
                  displayEmpty
                  size="small"
                  defaultValue=""
                  hiddenLabel
                  onChange={(event) =>
                    handleSingleChoiceSelectionTinyForm(
                      event.target.value,
                      formIndex,
                      sectionIndex,
                      questionIndex,
                      tinySectionIndex,
                      tinyQuestionIndex,
                    )
                  }
                  error={
                    !!getIn(formik.touched, fieldName) &&
                    !!getIn(formik.errors, fieldName)
                  }
                  sx={sxControlStyle}
                  disabled={isDisableAllFields}
                >
                  <MenuItem value="" disabled>
                    {t("commonDynamicForm.pleaseSelect")}
                  </MenuItem>
                  {question.data.option?.map((option, index) => (
                    <MenuItem key={index} value={option.value}>
                      {option.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {hasError && (
              <Typography color="error" variant="body2">
                {getIn(formik.errors, fieldName)}
              </Typography>
            )}
          </Box>
        );

      case EControlLabels.MULTIPLE_CHOICES:
        return (
          <Box key={fieldName} mb={2}>
            <Typography>{question.data.question}</Typography>

            {question.data.option?.map((option, index) => {
              // Get selected options from Formik (ensure it's always an array)
              let selectedOptions: string[] = [];
              const rawAnswer = getIn(formik.values, fieldName);

              if (rawAnswer && typeof rawAnswer === "string") {
                try {
                  selectedOptions = JSON.parse(rawAnswer) || [];
                  if (!Array.isArray(selectedOptions)) {
                    console.warn(
                      "⚠️ Parsed value is not an array:",
                      selectedOptions,
                    );
                    selectedOptions = [];
                  }
                } catch (error) {
                  console.error(
                    "⚠️ Error parsing answer:",
                    error,
                    "Raw Value:",
                    rawAnswer,
                  );
                }
              }

              const isSelected = selectedOptions.includes(option.value);

              return (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      value={option.value}
                      checked={isSelected}
                      onChange={(event) => {
                        let updatedSelection = [...selectedOptions];

                        // Update selection based on checkbox state
                        if (event.target.checked) {
                          updatedSelection.push(option.value); // Add to selection
                        } else {
                          updatedSelection = updatedSelection.filter(
                            (v) => v !== option.value,
                          ); // Remove from selection
                        }

                        // Store JSON string in Formik to match validation expectations
                        formik.setFieldValue(
                          fieldName,
                          JSON.stringify(updatedSelection),
                        );
                        formik.validateField(fieldName);

                        // Ensure question.answer stores all selected options
                        question.answer = JSON.stringify(updatedSelection);
                      }}
                      disabled={isDisableAllFields}
                    />
                  }
                  label={option.value}
                  sx={sxControlStyle}
                />
              );
            })}

            {/* Show error message if validation failed */}
            {getIn(formik.touched, fieldName) &&
              getIn(formik.errors, fieldName) && (
                <Typography color="error" variant="body2">
                  {getIn(formik.errors, fieldName)}
                </Typography>
              )}

            {/* <Divider style={{ margin: "16px 0" }} /> */}
          </Box>
        );

      case EControlLabels.FILE_UPLOAD:
        return (
          <Box key={fieldName} mb={2}>
            <Typography>{question.data.question}</Typography>
            {!getIn(formik.values, fieldName) && (
              <>
                <Button
                  variant="outlined"
                  component="label"
                  sx={sxControlStyle}
                  color="agriwhite"
                  className="custom-fileupload-btn"
                  disabled={isDisableAllFields}
                >
                  <i className="icon-upload"></i>
                  {t("commonDynamicForm.uploadFile")}
                  <input
                    type="file"
                    hidden
                    accept={getAcceptStringFromFileTypes(
                      question.data.fileTypes,
                    )}
                    onChange={(event) => {
                      const file = event.currentTarget.files?.[0];
                      uploadSeletcedFile(file!, fieldName);
                    }}
                    onBlur={formik.handleBlur}
                  />
                </Button>
                {/* Upload file hint text */}
                {question.data.fileTypes &&
                  question.data.fileTypes.length > 0 && (
                    <CommonFileUploadHint
                      supportedTypes={question.data.fileTypes}
                      maxFiles={1}
                      isImageOrVideo={false}
                    />
                  )}
              </>
            )}

            {getIn(formik.values, fieldName) &&
              getIn(formik.values, fieldName) !== "" && (
                <Box my={1} sx={{ display: "flex", alignItems: "center" }}>
                  <Box fontSize={"14px"} className="withoutspace-text-wrap">
                    {fileNames[fieldName] || getIn(formik.values, fieldName)}
                  </Box>
                  <IconButton
                    sx={{ marginLeft: 1 }}
                    onClick={() => {
                      formik.setFieldValue(fieldName, "");
                      formik.validateField(fieldName);
                      // Clear fileName from display state
                      setFileNames((prev) => {
                        const newState = { ...prev };
                        delete newState[fieldName];
                        return newState;
                      });
                    }}
                    disabled={isDisableAllFields}
                  >
                    <i className="icon-close" />
                  </IconButton>
                </Box>
              )}

            {hasError && (
              <Typography color="error" variant="body2">
                {getIn(formik.errors, fieldName)}
              </Typography>
            )}
          </Box>
        );

      case EControlLabels.DATE_PICKER:
        return (
          <Box key={fieldName} mb={2}>
            <Typography>{question.data.question}</Typography>
            <TextField
              type="date"
              size="small"
              hiddenLabel
              variant="filled"
              {...formik.getFieldProps(fieldName)}
              error={!!hasError}
              helperText={hasError && getIn(formik.errors, fieldName)}
              sx={{
                ...sxControlStyle,
                '& input[type="date"]': {
                  cursor: "pointer",
                },
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  cursor: "pointer",
                },
              }}
              disabled={isDisableAllFields}
            />
          </Box>
        );

      case EControlLabels.TIME_PICKER:
        return (
          <Box key={fieldName} mb={2}>
            <Typography>{question.data.question}</Typography>
            <TextField
              type="time"
              size="small"
              hiddenLabel
              variant="filled"
              value={formik.getFieldProps(fieldName).value || ""}
              onChange={(e) => {
                const timeOnly = e.target.value; // e.g., "14:30"
                formik.setFieldValue(fieldName, timeOnly);
              }}
              error={!!hasError}
              helperText={hasError && getIn(formik.errors, fieldName)}
              sx={{
                ...sxControlStyle,
                '& input[type="time"]': {
                  cursor: "pointer",
                },
                '& input[type="time"]::-webkit-calendar-picker-indicator': {
                  cursor: "pointer",
                },
              }}
              disabled={isDisableAllFields}
            />
          </Box>
        );

      case EControlLabels.RANGE_SCALE:
        return (
          <Box key={fieldName}>
            <Typography>{question.data.question}</Typography>
            <Box sx={sxControlStyle}>
              <Box className="preview-slider">
                {/* MUI Slider */}
                <Slider
                  value={parseFloat(getIn(formik.values, `${fieldName}`)) || 0}
                  min={
                    parseInt(
                      question.data?.rangeValues?.startRange as string,
                    ) || 0
                  }
                  max={
                    parseInt(question.data?.rangeValues?.endRange as string) ||
                    100
                  }
                  step={
                    parseInt(question.data?.rangeValues?.stepRange as string) ||
                    1
                  }
                  onChange={(_, newValue) => {
                    formik.setFieldValue(fieldName, String(newValue)); // Store as a string
                  }}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => value}
                  marks={getMarksForScaleRange(
                    parseInt(
                      question.data?.rangeValues?.startRange as string,
                    ) || 0,
                    parseInt(question.data?.rangeValues?.endRange as string) ||
                      100,
                  )}
                  disabled={isDisableAllFields}
                />
              </Box>
              {/* Display selected value */}
              <Box display="flex" alignItems="center" sx={sxControlStyle}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  paddingRight={1}
                >
                  {t("commonDynamicForm.selectedValue")}:
                </Typography>
                {parseFloat(getIn(formik.values, `${fieldName}`)) ||
                  parseInt(question.data?.rangeValues?.startRange as string) ||
                  0}
              </Box>
            </Box>
            {/* Input field for manual entry if enabled */}
            {question.data?.rangeValues?.isInputProvide && (
              <Box sx={sxControlStyle}>
                <Box mt={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t("commonDynamicForm.enterValue")}:
                  </Typography>
                </Box>

                {/* MUI TextField for manual input */}
                <TextField
                  fullWidth
                  hiddenLabel
                  value={getIn(formik.values, fieldName) || ""}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    // Allow only numeric input with optional decimal
                    if (
                      /^(\d*\.?\d*)$/.test(inputValue) &&
                      !inputValue.includes("..")
                    ) {
                      formik.setFieldValue(fieldName, String(inputValue));
                    }
                  }}
                  onBlur={() => {
                    // Trigger validation instead of auto-correcting
                    formik.validateField(fieldName);
                  }}
                  error={!!hasError}
                  helperText={hasError && getIn(formik.errors, fieldName)}
                  variant="filled"
                  size="small"
                  sx={{ mb: 2 }}
                  disabled={isDisableAllFields}
                />
              </Box>
            )}
          </Box>
        );

      // Add other control types like "MULTIPLE_CHOICES", "SINGLE_CHOICE", etc. as needed.

      default:
        return null;
    }
  };

  // Add the questionary to sub form
  const mapIQuestionaryToSubFrom = (form: any): ISubFroms => {
    return {
      formId: form.id,
      formVersion: form.version,
      formName: form.formName,
      formCategory: form.category || "CROP_CYCLE_ACTIVITY",
      sections: form.sections.map((section: any) => ({
        id: section.id,
        name: section.name,
        questions: section.questions.map((question: any) => ({
          id: question.id,
          fieldId: question.fieldId,
          label: question.label || "",
          typeOfControl: question.typeOfControl,
          data: question.data,
          answer: question.answer || "",
          answerId: question.answerId || "",
          subForms: question.subForms || [],
        })),
      })),
    };
  };

  // Get the selected tiny form form view
  const getSelectedFormsView = (
    formik: FormikProps<{ sections: ISection[] }>,
    sectionIndex: number,
    questionIndex: number,
  ) => {
    const question =
      formik.values.sections?.[sectionIndex]?.questions?.[questionIndex];
    if (!question) {
      return null;
    }
    const selectedValue = question.answer;
    if (!selectedValue) {
      return null;
    }

    const selectedOption = question.data.option?.find(
      (option: any) => option.value === selectedValue,
    );

    if (!selectedOption?.tinyForms?.length) {
      return null;
    }

    if (!question.subForms || question.subForms.length === 0) {
      return null;
    }
    return (
      <>
        {question.subForms.map((subForm, subFormIndex: number) =>
          subForm.sections.map((section, subFormSectionIndex: number) => (
            <Box
              key={section.id}
              sx={{
                marginTop: 2,
                padding: 2,
                border: "1px solid #ddd",
                borderRadius: "8px",
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                {section.name}
              </Typography>

              {section.questions.map((q, index) => (
                <Box key={q.id} mt={2}>
                  {typeof getRenderedTinyFormQuestion(
                    q,
                    subFormIndex,
                    sectionIndex,
                    questionIndex,
                    subFormSectionIndex,
                    index,
                  ) === "string" ? (
                    <Typography>
                      {getRenderedTinyFormQuestion(
                        q,
                        subFormIndex,
                        sectionIndex,
                        questionIndex,
                        subFormSectionIndex,
                        index,
                      )}
                    </Typography>
                  ) : (
                    getRenderedTinyFormQuestion(
                      q,
                      subFormIndex,
                      sectionIndex,
                      questionIndex,
                      subFormSectionIndex,
                      index,
                    )
                  )}
                </Box>
              ))}
            </Box>
          )),
        )}
      </>
    );
  };

  // Get marks list for scale range
  const getMarksForScaleRange = (start: number, end: number) => {
    const middle = Math.round((start + end) / 2);

    return [
      { value: start, label: `${start}` },
      { value: middle, label: `${middle}` },
      { value: end, label: `${end}` },
    ];
  };

  // Get rendered form fields
  const getRenderedQuestion = (
    question: IQuestion,
    sectionIndex: number,
    questionIndex: number,
  ) => {
    //console.log(question);
    const fieldName = `sections[${sectionIndex}].questions[${questionIndex}].answer`;
    const hasError =
      getIn(formik.errors, fieldName) && getIn(formik.touched, fieldName);

    switch (question.typeOfControl) {
      case EControlLabels.SHORT_ANSWER:
        return (
          <Box key={fieldName} mb={2}>
            <Typography>{question.data.question}</Typography>
            <TextField
              fullWidth
              variant="filled"
              hiddenLabel
              size="small"
              rows={1}
              placeholder={t("commonDynamicForm.textinputPlaceholder")}
              {...formik.getFieldProps(fieldName)}
              error={!!hasError}
              helperText={hasError && getIn(formik.errors, fieldName)}
              sx={sxControlStyle}
              disabled={isDisableAllFields}
            />
          </Box>
        );
      case EControlLabels.LONG_ANSWER:
        return (
          <Box key={fieldName} mb={2}>
            <Typography>{question.data.question}</Typography>
            <TextField
              fullWidth
              variant="filled"
              hiddenLabel
              size="small"
              multiline
              rows={4}
              placeholder={t("commonDynamicForm.textinputPlaceholder")}
              {...formik.getFieldProps(fieldName)}
              error={!!hasError}
              helperText={hasError && getIn(formik.errors, fieldName)}
              sx={sxControlStyle}
              disabled={isDisableAllFields}
            />
          </Box>
        );

      case EControlLabels.SINGLE_CHOICE:
        return (
          <Box key={fieldName} mb={2}>
            <Typography>{question.data.question}</Typography>
            {question.data.option && question.data.option.length <= 3 ? (
              <RadioGroup
                name={fieldName}
                value={
                  formik.values.sections?.[sectionIndex]?.questions?.[
                    questionIndex
                  ]?.answer || ""
                }
                onChange={(event) => {
                  formik.setFieldValue(fieldName, event.target.value);
                  handleSingleChoiceSelection(
                    event.target.value,
                    sectionIndex,
                    questionIndex,
                  );
                }}
              >
                {question.data.option?.map((option, index) => (
                  <FormControlLabel
                    key={index}
                    value={option.value}
                    control={<Radio />}
                    label={option.value}
                    disabled={isDisableAllFields}
                  />
                ))}
              </RadioGroup>
            ) : (
              <FormControl variant="filled" fullWidth margin="normal">
                <Select
                  name={fieldName}
                  value={
                    formik.values.sections?.[sectionIndex]?.questions?.[
                      questionIndex
                    ]?.answer || ""
                  }
                  displayEmpty
                  size="small"
                  defaultValue=""
                  hiddenLabel
                  onChange={(event) => {
                    formik.setFieldValue(fieldName, event.target.value);
                    handleSingleChoiceSelection(
                      event.target.value,
                      sectionIndex,
                      questionIndex,
                    );
                  }}
                  error={
                    !!getIn(formik.touched, fieldName) &&
                    !!getIn(formik.errors, fieldName)
                  }
                  sx={sxControlStyle}
                  disabled={isDisableAllFields}
                >
                  <MenuItem value="" disabled>
                    {t("commonDynamicForm.pleaseSelect")}
                  </MenuItem>
                  {question.data.option?.map((option, index) => (
                    <MenuItem key={index} value={option.value}>
                      {option.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {hasError && (
              <Typography color="error" variant="body2">
                {getIn(formik.errors, fieldName)}
              </Typography>
            )}

            {/* Render Tiny Form below the question if an option with tinyForms is selected */}
            {getSelectedFormsView(formik, sectionIndex, questionIndex)}
          </Box>
        );

      case EControlLabels.MULTIPLE_CHOICES:
        return (
          <Box key={fieldName} mb={2}>
            <Typography>{question.data.question}</Typography>

            {question.data.option?.map((option, index) => {
              // Get selected options from Formik (ensure it's always an array)
              let selectedOptions: string[] = [];
              const rawAnswer = getIn(formik.values, fieldName);

              if (rawAnswer && typeof rawAnswer === "string") {
                try {
                  selectedOptions = JSON.parse(rawAnswer) || [];
                  if (!Array.isArray(selectedOptions)) {
                    console.warn(
                      "⚠️ Parsed value is not an array:",
                      selectedOptions,
                    );
                    selectedOptions = [];
                  }
                } catch (error) {
                  console.error(
                    "⚠️ Error parsing answer:",
                    error,
                    "Raw Value:",
                    rawAnswer,
                  );
                }
              }

              const isSelected = selectedOptions.includes(option.value);

              return (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      value={option.value}
                      checked={isSelected}
                      onChange={(event) => {
                        let updatedSelection = [...selectedOptions];

                        // Update selection based on checkbox state
                        if (event.target.checked) {
                          updatedSelection.push(option.value); // Add to selection
                        } else {
                          updatedSelection = updatedSelection.filter(
                            (v) => v !== option.value,
                          ); // Remove from selection
                        }

                        // Store JSON string in Formik to match validation expectations
                        formik.setFieldValue(
                          fieldName,
                          JSON.stringify(updatedSelection),
                        );
                        formik.validateField(fieldName);

                        // Ensure question.answer stores all selected options
                        question.answer = JSON.stringify(updatedSelection);
                      }}
                      disabled={isDisableAllFields}
                    />
                  }
                  label={option.value}
                  sx={sxControlStyle}
                />
              );
            })}

            {/* Show error message if validation failed */}
            {getIn(formik.touched, fieldName) &&
              getIn(formik.errors, fieldName) && (
                <Typography color="error" variant="body2">
                  {getIn(formik.errors, fieldName)}
                </Typography>
              )}

            {/* <Divider style={{ margin: "16px 0" }} /> */}
          </Box>
        );

      case EControlLabels.FILE_UPLOAD:
        return (
          <Box key={fieldName} mb={2}>
            <Typography>{question.data.question}</Typography>
            {!getIn(formik.values, fieldName) && (
              <>
                <Button
                  variant="outlined"
                  component="label"
                  sx={sxControlStyle}
                  color="agriwhite"
                  className="custom-fileupload-btn"
                  disabled={isDisableAllFields}
                >
                  <i className="icon-upload"></i>
                  {t("commonDynamicForm.uploadFile")}
                  <input
                    type="file"
                    hidden
                    accept={getAcceptStringFromFileTypes(
                      question.data.fileTypes,
                    )}
                    onChange={(event) => {
                      const file = event.currentTarget.files?.[0];
                      uploadSeletcedFile(file!, fieldName);
                    }}
                    onBlur={formik.handleBlur}
                  />
                </Button>

                {/* Upload file hint text */}
                {question.data.fileTypes &&
                  question.data.fileTypes.length > 0 && (
                    <CommonFileUploadHint
                      supportedTypes={question.data.fileTypes}
                      maxFiles={1}
                      isImageOrVideo={false}
                    />
                  )}
              </>
            )}

            {getIn(formik.values, fieldName) &&
              getIn(formik.values, fieldName) !== "" && (
                <Box my={1} sx={{ display: "flex", alignItems: "center" }}>
                  <Box fontSize={"14px"} className="withoutspace-text-wrap">
                    {fileNames[fieldName] || getIn(formik.values, fieldName)}
                  </Box>
                  <IconButton
                    sx={{ marginLeft: 1 }}
                    onClick={() => {
                      formik.setFieldValue(fieldName, "");
                      formik.validateField(fieldName);
                      // Clear fileName from display state
                      setFileNames((prev) => {
                        const newState = { ...prev };
                        delete newState[fieldName];
                        return newState;
                      });
                    }}
                    disabled={isDisableAllFields}
                  >
                    <i className="icon-close" />
                  </IconButton>
                </Box>
              )}

            {hasError && (
              <Typography color="error" variant="body2">
                {getIn(formik.errors, fieldName)}
              </Typography>
            )}
          </Box>
        );

      case EControlLabels.DATE_PICKER:
        return (
          <Box key={fieldName} mb={2}>
            <Typography>{question.data.question}</Typography>
            <TextField
              type="date"
              size="small"
              hiddenLabel
              variant="filled"
              {...formik.getFieldProps(fieldName)}
              error={!!hasError}
              helperText={hasError && getIn(formik.errors, fieldName)}
              sx={{
                ...sxControlStyle,
                '& input[type="date"]': {
                  cursor: "pointer",
                },
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  cursor: "pointer",
                },
              }}
              disabled={isDisableAllFields}
            />
          </Box>
        );

      case EControlLabels.TIME_PICKER:
        return (
          <Box key={fieldName} mb={2}>
            <Typography>{question.data.question}</Typography>
            <TextField
              type="time"
              size="small"
              hiddenLabel
              variant="filled"
              value={formik.getFieldProps(fieldName).value || ""}
              onChange={(e) => {
                const timeOnly = e.target.value; // e.g., "14:30"
                formik.setFieldValue(fieldName, timeOnly);
              }}
              error={!!hasError}
              helperText={hasError && getIn(formik.errors, fieldName)}
              sx={{
                ...sxControlStyle,
                '& input[type="time"]': {
                  cursor: "pointer",
                },
                '& input[type="time"]::-webkit-calendar-picker-indicator': {
                  cursor: "pointer",
                },
              }}
              disabled={isDisableAllFields}
            />
          </Box>
        );
      case EControlLabels.RANGE_SCALE:
        return (
          <Box key={fieldName}>
            <Typography>{question.data.question}</Typography>
            <Box sx={sxControlStyle}>
              <Box className="preview-slider">
                {/* MUI Slider */}
                <Slider
                  value={parseFloat(getIn(formik.values, `${fieldName}`)) || 0}
                  min={
                    parseInt(
                      question.data?.rangeValues?.startRange as string,
                    ) || 0
                  }
                  max={
                    parseInt(question.data?.rangeValues?.endRange as string) ||
                    100
                  }
                  step={
                    parseInt(question.data?.rangeValues?.stepRange as string) ||
                    1
                  }
                  onChange={(_, newValue) => {
                    formik.setFieldValue(fieldName, String(newValue)); // Store as a string
                  }}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => value}
                  marks={getMarksForScaleRange(
                    parseInt(
                      question.data?.rangeValues?.startRange as string,
                    ) || 0,
                    parseInt(question.data?.rangeValues?.endRange as string) ||
                      100,
                  )}
                  disabled={isDisableAllFields}
                />
              </Box>
              {/* Display selected value */}
              <Box display="flex" alignItems="center">
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  paddingRight={1}
                >
                  {t("commonDynamicForm.selectedValue")}:
                </Typography>
                {parseFloat(getIn(formik.values, `${fieldName}`)) ||
                  parseInt(question.data?.rangeValues?.startRange as string) ||
                  0}
              </Box>
            </Box>
            {/* Input field for manual entry if enabled */}
            {question.data?.rangeValues?.isInputProvide && (
              <Box sx={sxControlStyle}>
                <Box mt={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t("commonDynamicForm.enterValue")}:
                  </Typography>
                </Box>

                {/* MUI TextField for manual input */}
                <TextField
                  fullWidth
                  hiddenLabel
                  value={getIn(formik.values, fieldName) || ""}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    // Allow valid decimal numbers only (e.g., 5, 5.5, .5, 0.5)
                    if (
                      /^(\d*\.?\d*)$/.test(inputValue) &&
                      !inputValue.includes("..")
                    ) {
                      formik.setFieldValue(fieldName, String(inputValue));
                    }
                  }}
                  onBlur={() => {
                    // Trigger validation instead of auto-correcting
                    formik.validateField(fieldName);
                  }}
                  error={!!hasError}
                  helperText={hasError && getIn(formik.errors, fieldName)}
                  variant="filled"
                  size="small"
                  disabled={isDisableAllFields}
                  sx={{ mb: 2 }}
                />
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ padding: 2 }}>
      {isLoading && <Loader />}
      <form onSubmit={formik.handleSubmit}>
        {formData?.sections?.map((section: ISection, sectionIndex: number) => (
          <Box key={section.id} sx={{ marginBottom: 4 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", marginBottom: 2 }}
            >
              {["Farmer Questionnaire"].includes(section.name)
                ? t("farmer.farmerQuestionnaire")
                : ["Field Questionnaire"].includes(section.name)
                  ? t("field.fieldQuestionnaire")
                  : section.name}
            </Typography>
            <Divider sx={{ marginBottom: 2 }} />
            {section.questions.map(
              (question: IQuestion, questionIndex: number) =>
                getRenderedQuestion(question, sectionIndex, questionIndex),
            )}
          </Box>
        ))}
        {!isHideSubmitBtn && (
          <Box sx={{ display: "flex", gap: 2 }}>
            {onCancel && (
              <Button variant="outlined" type="button" onClick={onCancel}>
                {t("commonFilter.cancel")}
              </Button>
            )}{" "}
            <Button
              variant="contained"
              type="submit"
              disabled={isDisableAllFields || (formData as any)?.isDeleted}
            >
              {t("commonDynamicForm.submitForm")}
            </Button>
          </Box>
        )}
      </form>
    </Box>
  );
};

export default DynamicForm;
